import React, { FC, useEffect, useMemo } from 'react';
import { useFilter } from './FilterContext';
import { useTableLegends } from './LegendContext';
import { usePagination } from './PaginationContext';
import { useSearch } from './SearchContext';
import { useSort } from './SortContext';
import { useTable } from './TableContext';
import { Filter } from '../../models/Filter';
import { PaginationModel } from '../../models/Pagination';
import { TableData } from '../../models/table/TableData';
import { SearchTerm } from '../../models/search/SearchTerm';

interface TableContextStateMonitorProps {
    onFilterChange?: (filters: Filter[]) => void;
    onSortChange?: () => void;
    onSearchChange?: (searchTerm: SearchTerm) => void;
    onPaginationChange?: (pagination: PaginationModel) => void;
    onLegendChange?: () => void;
    onTableChange?: (data: TableData[]) => void;
}

/**
 * This component is mainly used if we want to monitor the state of the table and do something when it changes.
 * For an example, if filters are changed we might want to update some component that falls outside or same level with table context.
 * PLEASE PLEASE PLEASE FUNCTIONS PASSED TO THIS COMPONENT SHOULDN'T UPDATE ANY OF THE CONTEXT.
 */
const TableContextStateMonitor: FC<TableContextStateMonitorProps> = ({ onFilterChange, onLegendChange, onPaginationChange, onSearchChange, onSortChange, onTableChange }) => {
    const tableContext = useTable();
    const filterContext = useFilter();
    const sortContext = useSort();
    const searchContext = useSearch();
    const paginationContext = usePagination();
    const legendContext = useTableLegends();

    const memoFilter = useMemo(() => filterContext.filters, [filterContext]);
    const memoSort = useMemo(() => sortContext, [sortContext]);
    const memoSearchTerm = useMemo(() => searchContext.searchTerm, [searchContext]);
    const memoPagination = useMemo(() => paginationContext.pagination, [paginationContext]);
    const memoLegend = useMemo(() => legendContext, [legendContext]);
    const memoTableData = useMemo(() => tableContext.tableData, [tableContext]);

    useEffect(() => {
        onFilterChange && onFilterChange(memoFilter);
    }, [memoFilter]);

    useEffect(() => {
        onSortChange && onSortChange();
    }, [memoSort]);

    useEffect(() => {
        onSearchChange && onSearchChange(memoSearchTerm);
    }, [memoSearchTerm]);

    useEffect(() => {
        onPaginationChange && onPaginationChange(memoPagination);
    }, [memoPagination]);

    useEffect(() => {
        onLegendChange && onLegendChange();
    }, [memoLegend]);

    useEffect(() => {
        onTableChange && onTableChange(memoTableData);
    }, [memoTableData]);

    return <>
    </>
}

export default TableContextStateMonitor;